import { useState } from 'react';
import { CheckCircle2, X, ChevronUp, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { QuickStatOverrideDialog } from '@/components/live-scoring/QuickStatOverrideDialog';
import type { MatchStatsResponse } from '@/api/feed/getFeedMatchStats';

interface OptionCardProps {
  questionId?: string;
  fixtureId?: string;
  statTypeCode?: string | null;
  option: {
    option_id?: string;
    option_text_en: string;
    option_text_fr?: string;
    option_image_en?: string | null;
    option_image_fr?: string | null;
    third_image_en?: string | null;
    third_image_fr?: string | null;
    is_correct: boolean;
    points?: number;
    selectionCount?: number;
    stats?: any;
    grading_data?: {
      playerId?: string | number;
      teamId?: string | number;
    };
  };
  language?: 'en' | 'fr';
  manualScoring?: boolean;
  matchStats?: MatchStatsResponse;
  onMarkCorrect?: (optionId: string, isCorrect: boolean) => void;
  onStatChange?: (optionId: string, statValue: number) => void;
  onSave?: (optionId: string) => void;
  onCancel?: (optionId: string) => void;
  onOverrideSuccess?: () => void;
  variant?: 'horizontal' | 'vertical'; // For prop questions with vertical layout
  showActions?: boolean; // Show Save/X buttons
}

export function OptionCard({
  questionId,
  fixtureId: fixtureIdProp,
  statTypeCode,
  option,
  language = 'en',
  manualScoring = false,
  matchStats,
  onStatChange,
  onSave,
  onCancel,
  onOverrideSuccess,
  variant = 'horizontal',
  showActions = false,
}: OptionCardProps) {
  const optionText =
    language === 'en' ? option.option_text_en : option.option_text_fr || option.option_text_en;
  const optionImage =
    language === 'en' ? option.option_image_en : option.option_image_fr || option.option_image_en;
  const thirdImage =
    language === 'en' ? option.third_image_en : option.third_image_fr || option.third_image_en;

  const isCorrect = option.is_correct;
  const points = option.points || 0;
  const [isEditing, setIsEditing] = useState(false);
  const [editingValue, setEditingValue] = useState('');
  const [isOverrideDialogOpen, setIsOverrideDialogOpen] = useState(false);

  // Extract playerId or teamId from grading_data
  const playerId = option.grading_data?.playerId?.toString();
  const teamId = option.grading_data?.teamId?.toString();

  // Get fixtureId from matchStats or prop (prop takes precedence)
  const fixtureId = fixtureIdProp || matchStats?.match.id;

  // Extract stat value for prominent display
  let statValue: number | null = null;
  if (option.stats) {
    if (Array.isArray(option.stats)) {
      // Team stats array - look for points first, then any numeric stat
      const pointsStat = option.stats.find((s: any) => s.statType === 'points');
      statValue = pointsStat?.value ?? option.stats[0]?.value ?? null;
    } else if (typeof option.stats === 'object' && option.stats !== null) {
      // Player stats object - prioritize common stats, then get first numeric value
      const statsObj = option.stats as Record<string, any>;
      // Try common stat keys first
      statValue =
        statsObj.rushYd ??
        statsObj.rushTd ??
        statsObj.passYd ??
        statsObj.passTd ??
        statsObj.recYd ??
        statsObj.recTd ??
        statsObj.points ??
        null;

      // If no common stat found, get first numeric value
      if (statValue === null) {
        const values = Object.values(option.stats);
        const numericValue = values.find((v): v is number => typeof v === 'number');
        statValue = numericValue ?? null;
      }
    }
  }

  const handleEditClick = () => {
    if (manualScoring) {
      // If we have questionId, fixtureId, (playerId or teamId), and statTypeCode, use QuickStatOverrideDialog
      if (questionId && fixtureId && (playerId || teamId) && statTypeCode) {
        setIsOverrideDialogOpen(true);
      } else {
        // Fallback to inline editing if we don't have all required data
        setEditingValue(statValue?.toString() || '0');
        setIsEditing(true);
      }
    }
  };

  const handleSave = () => {
    if (isEditing && option.option_id && onStatChange) {
      const numValue = parseFloat(editingValue);
      if (!isNaN(numValue)) {
        onStatChange(option.option_id, numValue);
      }
    }
    if (option.option_id && onSave) {
      onSave(option.option_id);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    if (option.option_id && onCancel) {
      onCancel(option.option_id);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (variant === 'vertical') {
    // Vertical layout for prop questions (per-option scoring)
    // Extract TD count or other stat for display
    let displayStat: number | null = statValue;
    if (option.stats && typeof option.stats === 'object' && !Array.isArray(option.stats)) {
      // Try to find TD count or other relevant stat
      const tdValue = (option.stats as any).rushTd ?? (option.stats as any).td ?? statValue;
      displayStat = typeof tdValue === 'number' ? tdValue : statValue;
    }

    return (
      <div
        className={`relative flex flex-col items-center p-4 rounded-lg border transition-colors ${
          isCorrect ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
        }`}
      >
        {/* Checkmark Icon - Top Left */}
        {isCorrect && (
          <div className="absolute top-2 left-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
          </div>
        )}

        {/* Option Image */}
        {(optionImage || thirdImage) && (
          <div className="mb-2">
            {optionImage && (
              <img
                src={optionImage}
                alt={optionText}
                className="h-16 w-16 object-contain rounded-full"
              />
            )}
            {thirdImage && !optionImage && (
              <img src={thirdImage} alt="" className="h-16 w-16 object-contain rounded-full" />
            )}
          </div>
        )}

        {/* Option Text */}
        <div className="text-center mb-2">
          <span className="text-sm font-medium">{optionText}</span>
        </div>

        {/* Stat Value and Points */}
        <div className="flex flex-col items-center gap-1 mt-auto">
          {displayStat !== null && (
            <div className="flex items-center justify-center gap-1">
              {isEditing ? (
                <>
                  <Input
                    type="number"
                    step="0.1"
                    value={editingValue}
                    onChange={e => setEditingValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="h-7 w-14 text-center text-sm"
                    autoFocus
                  />
                  <Button
                    type="button"
                    variant="default"
                    size="sm"
                    onClick={handleSave}
                    className="h-6 px-1 text-xs"
                  >
                    ✓
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleCancel}
                    className="h-6 w-6 p-0"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </>
              ) : (
                <>
                  <span className="text-base font-semibold">{displayStat}</span>
                  <ChevronUp className="h-3 w-3 text-gray-500" />
                  {manualScoring && (
                    <button
                      type="button"
                      onClick={handleEditClick}
                      className="p-0.5 hover:bg-gray-100 rounded transition-colors"
                      aria-label="Edit stat value"
                    >
                      <Pencil className="h-3 w-3 text-green-600" />
                    </button>
                  )}
                </>
              )}
            </div>
          )}
          <span className="text-xs text-gray-600">{points}pts</span>
        </div>

        {/* Quick Stat Override Dialog */}
        {questionId && fixtureId && (playerId || teamId) && statTypeCode && (
          <QuickStatOverrideDialog
            open={isOverrideDialogOpen}
            onOpenChange={setIsOverrideDialogOpen}
            questionId={questionId}
            fixtureId={fixtureId}
            playerId={playerId}
            teamId={teamId}
            statType={statTypeCode}
            currentValue={displayStat ?? undefined}
            onSuccess={() => {
              setIsOverrideDialogOpen(false);
              onOverrideSuccess?.();
            }}
          />
        )}
      </div>
    );
  }

  // Horizontal layout (default)
  return (
    <div
      className={`relative flex flex-col gap-2 p-3 rounded-lg border transition-colors ${
        isCorrect ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
      }`}
    >
      {/* Top Row: Checkmark, Image, Text, Actions */}
      <div className="flex items-center gap-3">
        {/* Checkmark Icon - Only show when correct */}
        {isCorrect && <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />}

        {/* Option Image */}
        {(optionImage || thirdImage) && (
          <div className="flex-shrink-0">
            {optionImage && (
              <img src={optionImage} alt={optionText} className="h-12 w-12 object-contain" />
            )}
            {thirdImage && !optionImage && (
              <img src={thirdImage} alt="" className="h-12 w-12 object-contain" />
            )}
          </div>
        )}

        {/* Option Text */}
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium">{optionText}</span>
        </div>

        {/* Action Buttons - Show when editing or showActions is true */}
        {(showActions || isEditing) && (
          <div className="flex items-center gap-1 flex-shrink-0">
            {onSave && (
              <Button
                variant="default"
                size="sm"
                onClick={handleSave}
                className="h-7 px-2 text-xs bg-blue-600 hover:bg-blue-700"
              >
                Save
              </Button>
            )}
            {onCancel && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancel}
                className="h-7 w-7 p-0 hover:bg-gray-100"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Stat Value - Center aligned below option */}
      {statValue !== null && (
        <div className="flex items-center justify-center gap-2">
          {isEditing ? (
            <>
              <Input
                type="number"
                step="0.1"
                value={editingValue}
                onChange={e => setEditingValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className="h-8 w-16 text-center"
                autoFocus
              />
              <Button
                type="button"
                variant="default"
                size="sm"
                onClick={handleSave}
                className="h-7 px-2 text-xs bg-blue-600 hover:bg-blue-700"
              >
                Save
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleCancel}
                className="h-7 w-7 p-0 hover:bg-gray-100"
              >
                <X className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <span className="text-lg font-semibold">{statValue}</span>
              <ChevronUp className="h-4 w-4 text-gray-500" />
              {manualScoring && (
                <button
                  type="button"
                  onClick={handleEditClick}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                  aria-label="Edit stat value"
                >
                  <Pencil className="h-4 w-4 text-green-600" />
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* Quick Stat Override Dialog */}
      {questionId && fixtureId && (playerId || teamId) && statTypeCode && (
        <QuickStatOverrideDialog
          open={isOverrideDialogOpen}
          onOpenChange={setIsOverrideDialogOpen}
          questionId={questionId}
          fixtureId={fixtureId}
          playerId={playerId}
          teamId={teamId}
          statType={statTypeCode}
          currentValue={statValue ?? undefined}
          onSuccess={() => {
            setIsOverrideDialogOpen(false);
            onOverrideSuccess?.();
          }}
        />
      )}
    </div>
  );
}
